import { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { reduxForm, Field, formValueSelector, change } from 'redux-form';
import { Typography, Grid, InputAdornment } from '@material-ui/core';
import { FormTextField } from '../../../../components/Form';
import { ModalWrapper } from '../../../../components';
import {
  BUYER_PURCHASE_METHODS,
  EASYPAY_PAYMENT_TYPES_FORMAT,
  EASYPAY_REQUEST_BUY_FEE_METHODS,
  FINALIZE_BUY_FEE_METHODS,
  FLOORING_FORM_COMPANIES_NAMES,
  PAYMENT_METHODS,
  PAYMENT_METHODS_STATUSES
} from '../../../../constants';
import { modalsToggle, listBuyerFees, updateEasyPayTransaction } from '../../../../actions';
import FinalizeAchFlooringApprovalPicker from './FinalizeAchFlooringApprovalPicker';
import FinalizeFeePaymentMethodPicker from './FinalizeFeePaymentMethodPicker';
import { maxLength, required } from '../../../../utils/validators';
import FinalizePaymentMethodPicker from './FinalizePaymentMethodPicker';
import { FinalizeFlooringCompanyPicker } from './FinalizeFlooringCompanyPicker';
import { FinalizeEasyPayStatus } from './FinalizeEasyPayStatus';
import { formatCurrency } from '../../../../utils';

const validateMaxLength = maxLength(100);

export function finalizeButtonConditionalDisable(vehicle) {
  return vehicle.isFinalized;
}

export function finalizeButtonConditionalRendering(vehicle) {
  return vehicle.toBeFinalized || vehicle.isFinalized;
}

export function finalizeDeclineButtonConditionalRendering(vehicle) {
  const easyPaid = vehicle.payment_method === PAYMENT_METHODS.EASY_PAY;
  return vehicle.isFinalized && easyPaid;
}

const modalName = 'finalizeSaleModal';

const StyledGrid = styled(Grid)`
  min-width: 500px;
  margin-top: 24px;
`;

const FieldGridContainer = styled(Grid)`
  min-width: 500px;
  margin-top: 24px;
  gap: 12px;
`;

const InfoWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, auto);
  justify-content: space-between;
  row-gap: 4px;
`;

const CarTitle = styled.div`
  font-weight: 700;
  font-style: Bold;
  font-size: 16px;
  color: #000;
`;

const VinLabel = styled.div`
  font-weight: 400;
  font-size: 14px;
  color: #999999;
`;

const SellerLabel = styled.div`
  font-weight: 400;
  font-size: 12px;
  color: #000;

  & > span {
    color: #3994de;
  }
`;

const WithBorder = styled.div`
  padding-bottom: 12px;
  border-bottom: 1px solid #949494;
  font-size: 18px;
`;

const AwardedBuyer = styled(WithBorder)`
  color: #000;
`;

const AwardPice = styled(WithBorder)`
  color: #666666;
`;

const TotalFee = styled(WithBorder)`
  color: #3994de;
`;

const Label = styled.div`
  color: #949494;
  font-size: 18px;
`;

class FinalizeSaleModal extends Component {
  componentDidMount() {
    const { getFees } = this.props;
    getFees();
  }

  handleSubmit = values => {
    const { vehicle, toggle, updateEasyPay, reset } = this.props;

    const easyPaid = vehicle.payment_method === PAYMENT_METHODS.EASY_PAY;

    const data = {
      id: vehicle.id,
      finalize_buy_fee_method: values.finalize_buy_fee_method,
      processing_fee: +values.processing_fee || 0,
      transportation_fee: +values.transportation_fee || 0,
      admin_finalize_notes: values.admin_finalize_notes || ''
    };

    if (easyPaid) {
      data.payment_status = values.payment_status;
    }

    if (values.finalize_payment_method) {
      data.finalize_payment_method = values.finalize_payment_method;
      data.finalize_flooring_company = values.finalize_flooring_company;
      data.finalize_other_message = values.finalize_other_message;
    }

    updateEasyPay(data);
    toggle(modalName);
    reset();
  };

  initializePrice = () => {
    const { vehicle, fees, changeFormField, form, finalize_buy_fee_method } = this.props;
    let buyFee = 0;
    const awardPrice = vehicle.vehicle_price;

    const buyerPurchaseMethods = vehicle.awarded_user?.buyer?.purchase_methods || [];
    const buyerHasAch = !!(
      buyerPurchaseMethods &&
      buyerPurchaseMethods.find(
        method =>
          method.option_name === BUYER_PURCHASE_METHODS.ACH && method.status !== PAYMENT_METHODS_STATUSES.DECLINED
      )
    );

    if (awardPrice && fees) {
      const matchingFee = fees.find(fee => +awardPrice >= +fee.min_amount && +awardPrice <= +fee.max_amount);
      if (matchingFee && matchingFee.fee) {
        buyFee = matchingFee.fee;
      }
    }
    const initProcessingFee =
      vehicle.buy_fee && (vehicle.buy_fee.processing_fee || vehicle.buy_fee.processing_fee === 0)
        ? vehicle.buy_fee.processing_fee
        : buyFee;
    const initTransportationFee =
      vehicle.buy_fee && vehicle.buy_fee.transportation_fee ? vehicle.buy_fee.transportation_fee : 0;
    changeFormField(form, 'processing_fee', initProcessingFee);
    changeFormField(form, 'transportation_fee', initTransportationFee);
    changeFormField(form, 'payment_status', PAYMENT_METHODS_STATUSES.APPROVED);
    if (finalize_buy_fee_method) changeFormField(form, 'finalize_buy_fee_method', '');

    const easyPaid = vehicle.payment_method === PAYMENT_METHODS.EASY_PAY;
    if (!easyPaid && buyerHasAch) {
      changeFormField(form, 'finalize_buy_fee_method', FINALIZE_BUY_FEE_METHODS.ACH);
      changeFormField(form, 'finalize_payment_method', FINALIZE_BUY_FEE_METHODS.ACH);
    }

    changeFormField(form, 'admin_finalize_notes', vehicle.admin_finalize_notes || '');
  };

  render() {
    const {
      vehicle,
      reset,
      transportation_fee: transportationFee,
      processing_fee: processingFee,
      handleSubmit,
      initialValues,
      finalize_payment_method: finalizePaymentMethod
    } = this.props;

    console.log(initialValues, 'initialValues');

    if (!vehicle || !vehicle.awarded_user || !vehicle.awarded_user.dealership_name) return null;

    const easyPaid = vehicle.payment_method === PAYMENT_METHODS.EASY_PAY;

    const paymentType = EASYPAY_PAYMENT_TYPES_FORMAT[vehicle.payment_type] || 'N/A';
    const paymentCompany = vehicle.payment_company ? `(${FLOORING_FORM_COMPANIES_NAMES[vehicle.payment_company]})` : '';
    const paymentComment = vehicle.payment_comment ? `(${vehicle.payment_comment})` : '';

    const easyPayApproved = vehicle.payment_status === PAYMENT_METHODS_STATUSES.APPROVED;
    const easyPayDeclined = vehicle.payment_status === PAYMENT_METHODS_STATUSES.DECLINED;
    const easyPayPendingApproval = !easyPayApproved && !easyPayDeclined;

    const requestedToIncludeBuyFee =
      vehicle.buy_fee &&
      vehicle.buy_fee.request_buy_fee_method === EASYPAY_REQUEST_BUY_FEE_METHODS.INCLUDE_IN_ACH_FLOORING;

    const requestedToPayBuyFeeByCreditCard =
      vehicle.buy_fee &&
      vehicle.buy_fee.request_buy_fee_method === EASYPAY_REQUEST_BUY_FEE_METHODS.PAY_WITH_CREDIT_CARD;

    let buyerPurchaseMethods;
    if (vehicle.awarded_user.buyer) {
      buyerPurchaseMethods = vehicle.awarded_user.buyer.purchase_methods || [];
    }
    const creditCardNumbers = vehicle.awarded_user?.buyer?.credit_card_last_numbers;
    const buyerHasAch = !!(
      buyerPurchaseMethods &&
      buyerPurchaseMethods.find(
        method =>
          method.option_name === BUYER_PURCHASE_METHODS.ACH && method.status !== PAYMENT_METHODS_STATUSES.DECLINED
      )
    );
    const buyerHasFlooring = !!(
      vehicle.awarded_user?.buyer?.flooring_form &&
      buyerPurchaseMethods &&
      buyerPurchaseMethods.find(
        method =>
          method.option_name === BUYER_PURCHASE_METHODS.FLOORING && method.status !== PAYMENT_METHODS_STATUSES.DECLINED
      )
    );
    const buyerHasCreditCard = !!(
      vehicle.awarded_user &&
      vehicle.awarded_user.buyer &&
      vehicle.awarded_user.buyer.customer_profile_id
    );
    const usdAmountInputProps = {
      startAdornment: <InputAdornment position="start">$</InputAdornment>,
      autoComplete: 'off',
      style: { paddingBottom: '5px', marginTop: '30px' }
    };

    const inputLabelProps = {
      style: {
        fontSize: '18px',
        lineHeight: '18px',
        color: '#949494',
        marginBottom: '12px',
        transform: 'translate(0, 1.5px) scale(1)'
      }
    };

    const showPaymentMethod = !easyPaid;

    return (
      <ModalWrapper
        modalId={modalName}
        title="Finalize Sale"
        submitText="Finalize"
        handleSubmit={handleSubmit(this.handleSubmit)}
        cancelAdditionalAction={reset}
        onEnter={this.initializePrice}
      >
        <InfoWrapper>
          <CarTitle>Car: {`${vehicle.year} ${vehicle.make} ${vehicle.model}`}</CarTitle>
          <FinalizeEasyPayStatus
            easyPaid={easyPaid}
            vehicle={vehicle}
            easyPayPendingApproval={easyPayPendingApproval}
            easyPayApproved={easyPayApproved}
            easyPayDeclined={easyPayDeclined}
          />
          <VinLabel>
            VIN:
            {' ' + vehicle.vin}
          </VinLabel>
          <SellerLabel>
            Seller: <span>{vehicle.user.dealership_name}</span>
          </SellerLabel>
        </InfoWrapper>

        <FieldGridContainer container direction="column">
          <Label>Buyer</Label>
          <AwardedBuyer>{vehicle.awarded_user.dealership_name}</AwardedBuyer>
        </FieldGridContainer>

        {easyPaid && (
          <FieldGridContainer container direction="column">
            <Label>ACH/Flooring Approval</Label>
            <FinalizeAchFlooringApprovalPicker />
            <a target="_blank" href={`/admin/customers/${vehicle.awarded_user.id}/docs`} rel="noopener noreferrer">
              View Docs
            </a>
          </FieldGridContainer>
        )}

        <FieldGridContainer container direction="column">
          <Label>Fee Payment Method</Label>
          {!easyPaid && (
            <FinalizeFeePaymentMethodPicker
              hasAch={buyerHasAch}
              creditCardNumbers={creditCardNumbers}
              hasFlooring={buyerHasFlooring}
              hasCreditCard={buyerHasCreditCard}
            />
          )}
          {easyPaid && requestedToIncludeBuyFee && (
            <Typography>
              {paymentType} {paymentCompany || paymentComment}
            </Typography>
          )}
          {easyPaid && requestedToPayBuyFeeByCreditCard && <Typography>Pay with credit card</Typography>}
        </FieldGridContainer>

        {showPaymentMethod && (
          <FieldGridContainer container direction="column">
            <Label>Payment Method</Label>
            <FinalizePaymentMethodPicker hasAch={buyerHasAch} hasFlooring={buyerHasFlooring} />
          </FieldGridContainer>
        )}

        {finalizePaymentMethod === 'flooring' && showPaymentMethod && (
          <StyledGrid style={{ marginTop: 0 }} container alignItems="center">
            <FinalizeFlooringCompanyPicker flooringForm={vehicle.awarded_user?.buyer?.flooring_form || {}} />
          </StyledGrid>
        )}

        {finalizePaymentMethod === 'other' && showPaymentMethod && (
          <StyledGrid style={{ marginTop: 0 }} container alignItems="center">
            <Field
              name="finalize_other_message"
              component={FormTextField}
              label="Payment comment"
              fullWidth
              validate={required}
              margin="normal"
              InputProps={{ style: usdAmountInputProps.style }}
              InputLabelProps={inputLabelProps}
            />
          </StyledGrid>
        )}

        <FieldGridContainer container direction="column">
          <Label>Award Price</Label>
          <AwardPice>{formatCurrency(vehicle.vehicle_price)}</AwardPice>
        </FieldGridContainer>

        <StyledGrid
          style={{ gap: '16px', marginTop: 0, flexWrap: 'nowrap', alignItems: 'flex-end' }}
          container
          alignItems="center"
        >
          <Grid item sm={4} xs={12}>
            <Field
              name="processing_fee"
              component={FormTextField}
              label="Processing Fee"
              fullWidth
              type="number"
              InputProps={usdAmountInputProps}
              InputLabelProps={inputLabelProps}
            />
          </Grid>

          <Grid item sm={4} xs={12}>
            <Field
              name="transportation_fee"
              component={FormTextField}
              label="Transportation Fee"
              fullWidth
              type="number"
              InputProps={usdAmountInputProps}
              InputLabelProps={inputLabelProps}
            />
          </Grid>

          <Grid item sm={4} xs={12} style={{ overflow: 'hidden' }}>
            <FieldGridContainer container direction="column">
              <Label>TOTAL FEE</Label>
              <TotalFee>{formatCurrency(+(processingFee || 0) + +(transportationFee || 0))}</TotalFee>
            </FieldGridContainer>
          </Grid>
        </StyledGrid>

        <FieldGridContainer container direction="column">
          <Label>Finalize Notes</Label>
          <Field
            validate={validateMaxLength}
            style={{ marginTop: '-6px' }}
            name="admin_finalize_notes"
            component={FormTextField}
            placeholder="Admin Finalize Notes"
            fullWidth
            InputLabelProps={{
              variant: 'standard',
              shrink: false,
              disableAnimation: true
            }}
          />
        </FieldGridContainer>
      </ModalWrapper>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const selector = formValueSelector(ownProps.form);
  return {
    fees: state.financials.fees,
    award_price: selector(state, 'award_price'),
    processing_fee: selector(state, 'processing_fee'),
    transportation_fee: selector(state, 'transportation_fee'),
    finalize_buy_fee_method: selector(state, 'finalize_buy_fee_method'),
    finalize_payment_method: selector(state, 'finalize_payment_method')
  };
};

const mapDispatchToProps = dispatch => ({
  getFees: async () => dispatch(listBuyerFees().request),
  toggle: key => dispatch(modalsToggle(key)),
  changeFormField: (form, field, value) => dispatch(change(form, field, value)),
  updateEasyPay: payload => dispatch(updateEasyPayTransaction(payload).request)
});

export default compose(
  reduxForm({
    initialValues: {
      payment_status: PAYMENT_METHODS_STATUSES.APPROVED,
      finalize_buy_fee_method: '',
      processing_fee: '0',
      transportation_fee: '0'
    }
  }),
  connect(mapStateToProps, mapDispatchToProps)
)(FinalizeSaleModal);
